import { useEffect, useState } from 'react';
import { getMyPayslip } from '../api/payroll';
import { fmtVND } from '../utils/format';

// Tháng mặc định = tháng TRƯỚC (lương thường chốt cho tháng đã qua).
function prevMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function Line({ label, value, strong, color, hint }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ color: 'var(--muted)' }}>{label}{hint ? <span className="small muted"> · {hint}</span> : null}</span>
      <span style={{ fontWeight: strong ? 700 : 500, color: color || 'inherit' }}>{value}</span>
    </div>
  );
}

export default function PayslipPage({ embedded = false }) {
  const [month, setMonth] = useState(prevMonth());
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showBhxh, setShowBhxh] = useState(false); // bấm dòng BHXH để bung chi tiết 8%/1,5%/1%
  const [showAdj, setShowAdj] = useState(false); // bấm dòng Thưởng/phạt để bung từng khoản

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    getMyPayslip(month)
      .then((d) => alive && setRow(d))
      .catch((e) => alive && setErr(e?.response?.data?.message || 'Lỗi tải bảng lương'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [month]);

  return (
    <div className={embedded ? '' : 'page'}>
      <div className="page-head" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {!embedded && <h2 style={{ margin: 0 }}>Bảng lương của tôi</h2>}
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
      </div>

      {loading && <div className="card">Đang tải…</div>}
      {err && <div className="card" style={{ color: 'var(--danger)' }}>{err}</div>}

      {!loading && !err && row && (
        <div className="card" style={{ maxWidth: 560 }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{row.name}</div>
              <div className="small muted">{row.employeeCode ? `Mã ${row.employeeCode} · ` : ''}{row.department || '—'} · tháng {month}</div>
            </div>
            {row.locked
              ? <span className="chip done">Đã chốt</span>
              : <span className="chip" title="Số liệu tạm tính, kế toán chưa chốt">Tạm tính</span>}
          </div>
          {!row.attendanceClosed && !row.locked && (
            <div className="small" style={{ color: 'var(--warn, #b06a00)', marginBottom: 8 }}>
              ⚠ Kế toán chưa chốt bảng chấm công — ngày công đang là số tự tính.
            </div>
          )}

          <div className="small muted" style={{ margin: '10px 0 2px', fontWeight: 700 }}>THU NHẬP</div>
          <Line label="Lương cơ bản" value={fmtVND(row.baseSalary)} />
          <Line label="Lương cơ bản thực nhận" hint={`${row.workDays}/${row.standardDays} công`} value={fmtVND(row.cbActual)} />
          <Line label="Phụ cấp" value={fmtVND(row.allowance)} />
          <Line label="Lương làm thêm giờ" value={fmtVND(row.overtimePay)} />
          {/* Phòng OP: vượt khoán sản lượng. Các phòng khác (PKD/DVKH/CSKH…): lương kinh doanh MH + VC. */}
          {row.department === 'Phòng OP' ? (
            <Line label="Vượt khoán sản lượng" value={fmtVND(row.overQuotaPay)} />
          ) : (
            <>
              <Line label="Lương kinh doanh MH" value={fmtVND(row.kinhDoanhMH)} />
              <Line label="Lương kinh doanh VC" value={fmtVND(row.kinhDoanhVC)} />
            </>
          )}
          <Line label="Tổng lương" value={fmtVND(row.totalSalary)} strong />

          <div className="small muted" style={{ margin: '14px 0 2px', fontWeight: 700 }}>KHẤU TRỪ</div>
          <div className="row" style={{ justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', cursor: row.payInsurance ? 'pointer' : 'default' }}
            onClick={() => row.payInsurance && setShowBhxh((v) => !v)} title={row.payInsurance ? 'Bấm để xem chi tiết từng khoản' : ''}>
            <span style={{ color: 'var(--muted)' }}>BHXH nhân viên (10,5%){row.payInsurance ? <span className="small muted"> {showBhxh ? '▲' : '▼'}</span> : null}</span>
            <span style={{ fontWeight: 500, color: row.payInsurance ? 'var(--danger)' : 'var(--muted)' }}>{row.payInsurance ? `− ${fmtVND(row.bhxhEmployee)}` : 'Không đóng'}</span>
          </div>
          {row.payInsurance && showBhxh && (
            <div style={{ padding: '4px 0 6px 14px' }}>
              {[['BHXH (8%)', 0.08], ['BHYT (1,5%)', 0.015], ['BHTN (1%)', 0.01]].map(([lb, rate]) => (
                <div key={lb} className="row" style={{ justifyContent: 'space-between', padding: '3px 0' }}>
                  <span className="small muted">• {lb}</span>
                  <span className="small" style={{ color: 'var(--danger)' }}>− {fmtVND(Math.round((row.insuranceBase || 0) * rate))}</span>
                </div>
              ))}
              <div className="small muted" style={{ paddingTop: 2 }}>Tính trên lương đóng BHXH: {fmtVND(row.insuranceBase)}</div>
            </div>
          )}
          <Line label="Kinh phí công đoàn (1%)" value={row.kpcd ? `− ${fmtVND(row.kpcd)}` : '—'} color={row.kpcd ? 'var(--danger)' : 'var(--muted)'} />
          {/* THƯỞNG/PHẠT: gộp 1 dòng, bấm vào để xem chi tiết từng khoản (+ thưởng, − phạt). */}
          {(row.adjustments || []).length > 0 && (
            <>
              <div className="row" style={{ justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                onClick={() => setShowAdj((v) => !v)} title="Bấm để xem chi tiết thưởng/phạt">
                <span style={{ color: 'var(--muted)' }}>Thưởng / phạt<span className="small muted"> {showAdj ? '▲' : '▼'}</span></span>
                <span style={{ fontWeight: 500, color: row.adjustTotal >= 0 ? 'var(--ok)' : 'var(--danger)' }}>
                  {row.adjustTotal >= 0 ? '+ ' : '− '}{fmtVND(Math.abs(row.adjustTotal))}
                </span>
              </div>
              {showAdj && (
                <div style={{ padding: '4px 0 6px 14px' }}>
                  {row.adjustments.map((a, i) => (
                    <div key={i} className="row" style={{ justifyContent: 'space-between', padding: '3px 0', gap: 10 }}>
                      <span className="small muted">• {a.label}</span>
                      <span className="small" style={{ color: a.amount >= 0 ? 'var(--ok)' : 'var(--danger)', whiteSpace: 'nowrap' }}>
                        {a.amount >= 0 ? '+ ' : '− '}{fmtVND(Math.abs(a.amount))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="row" style={{ justifyContent: 'space-between', marginTop: 14, paddingTop: 12, borderTop: '2px solid var(--border)' }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>THỰC NHẬN</span>
            <span style={{ fontWeight: 800, fontSize: 20, color: 'var(--accent)' }}>{fmtVND(row.netPay)}</span>
          </div>
          {row.note && <div className="small muted" style={{ marginTop: 10 }}>Ghi chú: {row.note}</div>}
        </div>
      )}
    </div>
  );
}
